import { supabase } from '../../../lib/supabase';
import { CLIENT_ID } from '../../../lib/client-context';
import { logger } from '../../../utils/logger';
import { cacheUser } from '../../../utils/userCache';
import { debounce } from '../../../lib/utils';
import { RealtimeChannel } from '@supabase/supabase-js';

// Cache for collaborator data to prevent multiple API calls
const collaboratorsCache: Record<string, { data: Collaborator[]; timestamp: number }> = {};

// Track active realtime subscriptions
const activeSubscriptions: Record<string, { channel: RealtimeChannel; lastEventTime: number }> = {};

export type Collaborator = {
  id: number;
  project_id: string;
  user_id: string;
  role: string;
  users_with_details?: {
    id: string;
    email: string;
    display_name?: string;
    avatar_url?: string;
  } | null;
};

export type Invitation = {
  id: string;
  project_id: string;
  invitee_email: string;
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
};

/**
 * Fetches the list of collaborators for a given project, including user details.
 *
 * @param projectId The UUID of the project
 * @returns A list of Collaborator objects with user details
 */
export const listCollaborators = async (projectId: string): Promise<Collaborator[]> => {
  try {
    // Check if projectId is provided
    if (!projectId) {
      logger.error('Missing project ID in listCollaborators');
      return []; // Return empty array rather than failing
    }

    // Check if we have cached data that's less than 5 minutes old
    const cacheEntry = collaboratorsCache[projectId];
    const now = Date.now();
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

    // When offline, always use cached data if available, regardless of age
    if (!navigator.onLine && cacheEntry) {
      logger.store('Using cached collaborators data (offline mode)', {
        projectId,
        cacheAge: now - cacheEntry.timestamp,
      });
      return cacheEntry.data;
    } else if (cacheEntry && now - cacheEntry.timestamp < CACHE_TTL) {
      // Online but within cache TTL
      logger.store('Using cached collaborators data', {
        projectId,
        cacheAge: now - cacheEntry.timestamp,
      });
      return cacheEntry.data;
    }

    // Don't try to fetch if offline - return empty array or cached data
    if (!navigator.onLine) {
      logger.store('Skip fetching collaborators (offline) - returning empty array', { projectId });
      return cacheEntry?.data || []; // Return cached data or empty array
    }

    logger.fetch('Fetching collaborators via edge function', { projectId });

    // Use the secure edge function instead of directly querying the restricted view
    const { data, error } = await supabase.functions.invoke('get-project-collaborators', {
      body: { projectId },
    });

    if (error) {
      logger.error('Error calling get-project-collaborators edge function', {
        error: JSON.stringify(error),
        statusText: error.message,
        projectId,
      });
      throw error;
    }

    if (!data || !data.data) {
      logger.warn('No collaborator data returned from edge function', { projectId });
      return [];
    }

    // Transform the data from the edge function to match our expected structure
    const collaborators: Collaborator[] = data.data.map((collab: any) => ({
      id: collab.id,
      project_id: collab.project_id,
      user_id: collab.user_id,
      role: collab.role,
      users_with_details: collab.users_with_details || null,
    }));

    // Update the cache
    collaboratorsCache[projectId] = {
      data: collaborators,
      timestamp: now,
    };

    return collaborators;
  } catch (mainError) {
    logger.error('Error in listCollaborators', {
      error: mainError instanceof Error ? mainError.message : String(mainError),
      projectId,
    });
    
    // If offline and we have cache, return that instead of throwing
    if (!navigator.onLine && collaboratorsCache[projectId]) {
      logger.store('Returning cached data after fetch error (offline)', { projectId });
      return collaboratorsCache[projectId].data;
    }
    
    throw mainError;
  }
};

/**
 * Checks if a project has any collaborators.
 *
 * @param projectId The UUID of the project
 * @returns Boolean indicating if the project has collaborators
 */
export const checkProjectHasCollaborators = async (projectId: string): Promise<boolean> => {
  // Skip request if offline - assume no collaborators
  if (!navigator.onLine) {
    logger.store('Skip checking collaborators (offline)', { projectId });
    return false;
  }

  try {
    const { error, count } = await supabase
      .from('project_collaborators')
      .select('*', { count: 'exact' })
      .eq('project_id', projectId);

    if (error) {
      logger.error('Error checking project collaborators', {
        error: error.message,
        projectId,
      });
      return false;
    }

    return count > 0;
  } catch (error) {
    // Log error but don't fail the app
    logger.error('Exception checking project collaborators', {
      error: error instanceof Error ? error.message : String(error),
      projectId,
    });
    return false;
  }
};

/**
 * Adds a new collaborator to a project.
 *
 * @param projectId The UUID of the project
 * @param userId The UUID or identifier of the user to add
 * @param role The role of the new collaborator (default: 'guest')
 * @returns The newly added Collaborator object
 */
export const addCollaborator = async (
  projectId: string,
  userId: string,
  role: string = 'guest'
): Promise<Collaborator> => {
  const { data, error } = await supabase
    .from('project_collaborators')
    .insert([
      {
        project_id: projectId,
        user_id: userId,
        role,
        metadata: {
          client_id: CLIENT_ID,
          added_at: new Date().toISOString(),
        },
      },
    ])
    .single();

  if (error) {
    throw error;
  }
  return data;
};

/**
 * Lists pending invitations for a project
 *
 * @param projectId The UUID of the project
 * @returns A list of pending invitations
 */
export const listPendingInvitations = async (projectId: string): Promise<Invitation[]> => {
  try {
    // Query only the fields we need
    const { data, error } = await supabase
      .from('project_invitations')
      .select('id, project_id, invitee_email, status, created_at')
      .eq('project_id', projectId)
      .eq('status', 'pending');

    if (error) {
      logger.error('Error fetching invitations', {
        error: error.message,
        projectId,
      });
      throw error;
    }

    return data || [];
  } catch (err) {
    logger.error('Exception in listPendingInvitations', {
      error: err instanceof Error ? err.message : String(err),
      projectId,
    });
    throw err;
  }
};

/**
 * Preloads collaborator data for a project and populates the user cache
 *
 * This function fetches all collaborators for a project and adds their data
 * to the user cache, ensuring that assignee info is available when loading tasks.
 * It also sets up real-time subscriptions to collaborator changes.
 *
 * @param projectId The UUID of the project to preload collaborators for
 * @returns Promise that resolves when preloading is complete
 */
export const preloadCollaborators = async (projectId: string): Promise<void> => {
  try {
    logger.store('Preloading collaborators for project', { projectId });

    // Get collaborators (using existing cache if available)
    const collaborators = await listCollaborators(projectId);

    // Store each user in the user cache for use in other components
    collaborators.forEach(collab => {
      if (collab.user_id && collab.users_with_details) {
        cacheUser(collab.user_id, {
          id: collab.user_id,
          email: collab.users_with_details.email || '',
          display_name: collab.users_with_details.display_name || '',
          avatar_url: collab.users_with_details.avatar_url || undefined,
        });
      }
    });

    // Set up real-time subscription if not already active
    subscribeToCollaboratorChanges(projectId);

    logger.store('Successfully preloaded collaborators for project', {
      projectId,
      count: collaborators.length,
    });
  } catch (error) {
    logger.error('Failed to preload collaborators', {
      error: error instanceof Error ? error.message : String(error),
      projectId,
    });
    // Don't throw the error - preloading should be non-blocking
  }
};

/**
 * Sets up a real-time subscription to collaborator changes for a project
 *
 * @param projectId The project ID to subscribe to collaborator changes for
 */
export const subscribeToCollaboratorChanges = (projectId: string): void => {
  // Skip if already subscribed
  if (activeSubscriptions[projectId]) {
    return;
  }

  try {
    // Skip subscription if offline
    if (!navigator.onLine) {
      logger.realtime('Skipping collaborator subscription - device is offline', { projectId });
      return;
    }
    
    logger.realtime('Setting up collaborator subscription', { projectId });

    // Create a new subscription
    const channel = supabase
      .channel(`project-collaborators-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen for all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'project_collaborators',
          filter: `project_id=eq.${projectId}`,
        },
        async payload => {
          // Skip processing if offline
          if (!navigator.onLine) {
            logger.realtime('Skipping collaborator change processing - device is offline', { 
              projectId,
              eventType: payload.eventType
            });
            return;
          }
          
          logger.realtime('Collaborator change detected', {
            projectId,
            eventType: payload.eventType,
            timestamp: new Date().toISOString(),
          });

          // Update timestamp for throttling
          if (activeSubscriptions[projectId]) {
            activeSubscriptions[projectId].lastEventTime = Date.now();
          }

          // Clear cache to force a refresh on next fetch
          if (collaboratorsCache[projectId]) {
            delete collaboratorsCache[projectId];
          }

          // Refresh collaborator data
          await silentlyRefreshCollaborators(projectId);
        }
      )
      .subscribe((function() {
        // Use debounced function for status logging to prevent flooding
        const debouncedStatusLogger = debounce((status) => {
          // Skip logging when offline
          if (status === 'CHANNEL_ERROR' && !navigator.onLine) {
            return;
          }
          
          logger.realtime('Collaborator subscription status', { projectId, status });
        }, 5000); // 5 second debounce
        
        return (status) => {
          // Use the debounced logger
          debouncedStatusLogger(status);
        };
      })());

    // Store the subscription
    activeSubscriptions[projectId] = {
      channel,
      lastEventTime: Date.now(),
    };
  } catch (error) {
    logger.error('Error setting up collaborator subscription', {
      projectId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

/**
 * Silently refreshes collaborator data without showing loading indicators
 *
 * @param projectId The project ID to refresh collaborators for
 */
export const silentlyRefreshCollaborators = async (projectId: string): Promise<void> => {
  try {
    // Skip if offline - prevents unnecessary requests and errors
    if (!navigator.onLine) {
      logger.store('Skipping collaborator refresh - device is offline', { projectId });
      return;
    }
    
    // Skip if throttled (prevent multiple rapid refreshes)
    const lastRefresh = collaboratorsCache[projectId]?.timestamp || 0;
    const now = Date.now();

    if (now - lastRefresh < 2000) {
      // 2 second throttle
      logger.store('Skipping collaborator refresh (throttled)', { projectId });
      return;
    }

    logger.store('Silently refreshing collaborators', { projectId });

    // Force a fresh fetch by clearing the cache entry
    delete collaboratorsCache[projectId];

    // Fetch fresh data
    const collaborators = await listCollaborators(projectId);

    // Update user cache with fresh data
    collaborators.forEach(collab => {
      if (collab.user_id && collab.users_with_details) {
        cacheUser(collab.user_id, {
          id: collab.user_id,
          email: collab.users_with_details.email || '',
          display_name: collab.users_with_details.display_name || '',
          avatar_url: collab.users_with_details.avatar_url || undefined,
        });
      }
    });

    logger.store('Successfully refreshed collaborators', {
      projectId,
      count: collaborators.length,
    });
  } catch (error) {
    logger.error('Failed to silently refresh collaborators', {
      error: error instanceof Error ? error.message : String(error),
      projectId,
    });
  }
};

/**
 * Unsubscribes from collaborator changes for a project
 *
 * @param projectId The project ID to unsubscribe from
 */
export const unsubscribeFromCollaboratorChanges = (projectId: string): void => {
  try {
    if (activeSubscriptions[projectId]) {
      logger.realtime('Unsubscribing from collaborator changes', { projectId });

      // Remove the subscription
      activeSubscriptions[projectId].channel.unsubscribe();
      delete activeSubscriptions[projectId];
    }
  } catch (error) {
    logger.error('Error unsubscribing from collaborator changes', {
      projectId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

// These functions have been moved to a secure Supabase Edge Function
// See: /supabase/functions/check-collaborator-permissions/index.ts
